// components/models/MagnificImageUpscaleControlPanel.tsx
import React, { useCallback } from 'react';
import Select from '../ui/Select';
import RangeControl from '../ui/RangeControl';
import Switch from '../ui/Switch';
import {
  MagnificImageUpscaleFlavor,
  MagnificImageUpscaleParams,
} from '#types/types';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { numberOrDefault } from '#lib/utils';

const defaultMagnificImageUpscaleParams: MagnificImageUpscaleParams = {
  prompt: '',
  scale_factor: 2,
  sharpen: 7,
  smart_grain: 7,
  ultra_detail: 30,
  flavor: 'photo',
  filter_nsfw: false,
  seed: -1,
};

export const getDefaultMagnificImageUpscaleParams = (): MagnificImageUpscaleParams => ({
  ...defaultMagnificImageUpscaleParams,
});

const flavorOptions: MagnificImageUpscaleFlavor[] = ['photo', 'photo_denoiser', 'sublime'];

const MagnificImageUpscaleControlPanel: React.FC = React.memo(() => {
  const { kernel } = useKernel();
  const params = useKernelStore(
    state => state.modelParams[state.diffusionModel] as MagnificImageUpscaleParams | undefined
  );

  const updateParams = useCallback((
    name: keyof MagnificImageUpscaleParams,
    value: MagnificImageUpscaleParams[keyof MagnificImageUpscaleParams]
  ) => {
    const diffusionModel = kernel.current?.getDiffusionModel();
    if (!diffusionModel) return;

    kernel.current?.setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[MagnificImageUpscaleControlPanel] Missing params for model "${diffusionModel}".`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: value,
        },
      };
    });

    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value,
      model_name: diffusionModel,
      source_component: 'MagnificImageUpscaleControlPanel',
    });
  }, [kernel]);

  const handleFlavorChange = useCallback((value: string) => {
    updateParams('flavor', value as MagnificImageUpscaleFlavor);
  }, [updateParams]);

  const handleRangeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    updateParams(name as keyof MagnificImageUpscaleParams, Number(value));
  }, [updateParams]);

  const handleNsfwToggle = useCallback(() => {
    const diffusionModel = kernel.current?.getDiffusionModel();
    const currentParams = diffusionModel
      ? kernel.current?.getModelParams()[diffusionModel] as MagnificImageUpscaleParams | undefined
      : undefined;
    updateParams('filter_nsfw', !(currentParams?.filter_nsfw === true));
  }, [kernel, updateParams]);

  if (!params) return null;

  return (
    <div className="space-y-4">
      <RangeControl
        label="Scale Factor"
        name="scale_factor"
        min={2}
        max={16}
        step={1}
        value={numberOrDefault(params.scale_factor, 2)}
        onChange={handleRangeChange}
      />
      <Select
        label="Flavor"
        value={params.flavor ?? 'photo'}
        options={flavorOptions}
        onChange={handleFlavorChange}
      />
      <RangeControl
        label="Sharpen"
        name="sharpen"
        min={0}
        max={100}
        step={1}
        value={numberOrDefault(params.sharpen, 7)}
        onChange={handleRangeChange}
      />
      <RangeControl
        label="Smart Grain"
        name="smart_grain"
        min={0}
        max={100}
        step={1}
        value={numberOrDefault(params.smart_grain, 7)}
        onChange={handleRangeChange}
      />
      <RangeControl
        label="Ultra Detail"
        name="ultra_detail"
        min={0}
        max={100}
        step={1}
        value={numberOrDefault(params.ultra_detail, 30)}
        onChange={handleRangeChange}
      />
      <div className="flex items-center space-x-2">
        <Switch
          checked={params.filter_nsfw === true}
          onCheckedChange={handleNsfwToggle}
          labelRight="Filter NSFW"
        />
      </div>
    </div>
  );
});

MagnificImageUpscaleControlPanel.displayName = 'MagnificImageUpscaleControlPanel';

export default MagnificImageUpscaleControlPanel;
